import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ThemeToggle from '../components/ThemeToggle';
import SiteFooter from '../components/SiteFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    inviteCode: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [prefilledFromLink, setPrefilledFromLink] = useState(false);

  // Prefill from an admin-generated invite link:
  //   /register?email=someone@example.com&code=AB12CD34
  // Runs mount-only — we never want a URL edit to clobber typed input mid-flow.
  //
  // Invite-only gate: if the user landed here WITHOUT a prefilled email + code
  // (i.e. typed /register directly, or clicked a stale link from an older
  // build of /login), send them back to the landing page where the only
  // legitimate entry point for new users lives — the "Request beta access"
  // panel. /register is not a general-purpose signup surface.
  useEffect(() => {
    const emailParam = (searchParams.get('email') || '').trim();
    const codeParam = (searchParams.get('code') || '').trim().toUpperCase();
    if (!emailParam || !codeParam) {
      navigate('/', { replace: true });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      email: emailParam,
      inviteCode: codeParam,
    }));
    setPrefilledFromLink(true);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.inviteCode || !formData.password) {
      toast.error('All fields are required');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        invite_code: formData.inviteCode,
        password: formData.password
      });
      
      // Store token
      localStorage.setItem('sage_token', response.data.access_token);
      localStorage.setItem('sage_user', JSON.stringify(response.data.user));
      
      toast.success('Registration successful! Welcome to SAGE.');
      setTimeout(() => navigate('/onboarding'), 1500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      <div className="overlay-noise" />
      <div className="fixed top-5 right-5 z-30">
        <ThemeToggle size="sm" className="border border-white/14 bg-stone-950/40 backdrop-blur-sm rounded-sm" />
      </div>
      
      <div className="relative z-10 w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 
            className="text-5xl md:text-6xl font-light tracking-tight text-zinc-100 mb-4"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="register-title"
          >
            Enter SAGE
          </h1>
          <p className="text-sm font-mono uppercase tracking-widest text-zinc-400">
            Complete Your Registration
          </p>
          {prefilledFromLink && (
            <p
              className="mt-4 text-xs text-zinc-300 bg-white/5 border border-white/10 rounded-sm px-3 py-2 inline-block"
              data-testid="prefilled-from-link-banner"
            >
              We've prefilled your email and invite code from the invitation link. Just pick a password to continue.
            </p>
          )}
        </div>

        {/* Registration Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="registration-form">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
              Email
            </label>
            <Input
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 w-full font-light"
              data-testid="register-email-input"
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
              Invite Code
            </label>
            <Input
              type="text"
              placeholder="XXXXXXXX"
              value={formData.inviteCode}
              onChange={(e) => setFormData({...formData, inviteCode: e.target.value.toUpperCase()})}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 w-full font-mono tracking-widest"
              data-testid="invite-code-input"
              disabled={loading}
              maxLength={8}
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
              Password
            </label>
            <Input
              type="password"
              placeholder="Create password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 w-full font-light"
              data-testid="password-input"
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-zinc-400 mb-2 block">
              Confirm Password
            </label>
            <Input
              type="password"
              placeholder="Confirm password"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              className="bg-transparent border-b border-white/20 focus:border-white text-white placeholder:text-zinc-500 px-0 py-4 rounded-none focus:ring-0 w-full font-light"
              data-testid="confirm-password-input"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="threshold-button w-full rounded-sm px-8 py-5 text-lg md:text-xl mt-8"
            data-testid="register-submit-button"
            disabled={loading}
            aria-label="Begin Journey"
          >
            <span>{loading ? 'Registering…' : 'Begin Journey'}</span>
            {!loading && (
              <span className="threshold-button-arrow" aria-hidden="true">→</span>
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <a 
            href="/" 
            className="text-xs text-zinc-500 hover:text-zinc-500 transition-colors"
          >
            ← Back to Landing
          </a>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
};

export default RegisterPage;